/**
 * Row-Based Selectors for UI-Model-1
 *
 * Memoization-friendly selectors for container row operations.
 * These selectors work with the existing UIModelState structure.
 */

import type { UIModelState, ContainerAPI } from '../../types/state.types';
import type { ContainerRow, SlotAvailability, RowStats } from '../types/container-row.types';
import { CONTAINER_CONSTRAINTS } from '../constants/constraints';
import {
  getContainerRows,
  getNextAvailableSlot,
  canAddToRow,
  getRowContainerCount,
  groupContainersByRow,
  getRowStats,
} from '../utils/rowCalculations';
import { getNestingInfo } from '../utils/containerTraversal';
import * as baseSelectors from '../../selectors';

// ============================================================================
// Canvas Container Selectors
// ============================================================================

/**
 * Selects containers grouped by row for a canvas
 *
 * @param state - UIModelState
 * @param canvasId - Canvas ID
 * @returns Map of row index to containers
 */
export const selectContainersByRow = (
  state: UIModelState,
  canvasId: string
): Map<number, ContainerAPI[]> => {
  const containers = baseSelectors.selectContainersForCanvas(state, canvasId);
  const grouped = groupContainersByRow(containers);
  return new Map(Object.entries(grouped).map(([k, v]) => [Number(k), v]));
};

/**
 * Selects container rows (ContainerRow[]) for a canvas
 * Ready for UI rendering
 *
 * @param state - UIModelState
 * @param canvasId - Canvas ID
 * @returns Array of ContainerRow objects sorted by row index
 */
export const selectContainerRows = (
  state: UIModelState,
  canvasId: string
): ContainerRow[] => {
  const containers = baseSelectors.selectContainersForCanvas(state, canvasId);
  return getContainerRows(containers, CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW);
};

/**
 * Selects container rows for the active canvas
 *
 * @param state - UIModelState
 * @returns Array of ContainerRow objects or empty array
 */
export const selectActiveContainerRows = (state: UIModelState): ContainerRow[] => {
  const canvasId = baseSelectors.selectActiveCanvasId(state);
  if (!canvasId) return [];
  return selectContainerRows(state, canvasId);
};

// ============================================================================
// Row Availability Selectors
// ============================================================================

/**
 * Checks if a specific row can accept more containers
 *
 * @param state - UIModelState
 * @param canvasId - Canvas ID
 * @param rowIndex - Row index to check
 * @returns Whether the row can accept more containers
 */
export const selectIsRowFull = (
  state: UIModelState,
  canvasId: string,
  rowIndex: number
): boolean => {
  const containers = baseSelectors.selectContainersForCanvas(state, canvasId);
  return !canAddToRow(containers, rowIndex, CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW);
};

/**
 * Checks if a specific row in the active canvas is full
 *
 * @param state - UIModelState
 * @param rowIndex - Row index to check
 * @returns Whether the row is full
 */
export const selectIsActiveRowFull = (
  state: UIModelState,
  rowIndex: number
): boolean => {
  const canvasId = baseSelectors.selectActiveCanvasId(state);
  if (!canvasId) return true; // No canvas = can't add
  return selectIsRowFull(state, canvasId, rowIndex);
};

/**
 * Selects the next available slot for adding a container
 *
 * @param state - UIModelState
 * @param canvasId - Canvas ID
 * @returns Slot availability information
 */
export const selectNextAvailableSlot = (
  state: UIModelState,
  canvasId: string
): SlotAvailability => {
  const containers = baseSelectors.selectContainersForCanvas(state, canvasId);
  return getNextAvailableSlot(containers, CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW);
};

/**
 * Selects the next available slot for the active canvas
 *
 * @param state - UIModelState
 * @returns Slot availability information
 */
export const selectActiveNextAvailableSlot = (state: UIModelState): SlotAvailability | null => {
  const canvasId = baseSelectors.selectActiveCanvasId(state);
  if (!canvasId) return null;
  return selectNextAvailableSlot(state, canvasId);
};

/**
 * Selects the count of containers in a specific row
 *
 * @param state - UIModelState
 * @param canvasId - Canvas ID
 * @param rowIndex - Row index
 * @returns Number of containers in the row
 */
export const selectRowContainerCount = (
  state: UIModelState,
  canvasId: string,
  rowIndex: number
): number => {
  const containers = baseSelectors.selectContainersForCanvas(state, canvasId);
  return getRowContainerCount(containers, rowIndex);
};

// ============================================================================
// Nested Container Row Selectors
// ============================================================================

/**
 * Selects nested containers grouped by row within a parent container
 *
 * @param state - UIModelState
 * @param parentContainerId - Parent container ID
 * @returns Map of row index to nested containers
 */
export const selectNestedContainersByRow = (
  state: UIModelState,
  parentContainerId: string
): Map<number, ContainerAPI[]> => {
  const nestedContainers = baseSelectors.selectChildContainers(state, parentContainerId);
  const grouped = groupContainersByRow(nestedContainers);
  return new Map(Object.entries(grouped).map(([k, v]) => [Number(k), v]));
};

/**
 * Selects nested container rows for a parent container
 *
 * @param state - UIModelState
 * @param parentContainerId - Parent container ID
 * @returns Array of ContainerRow objects
 */
export const selectNestedContainerRows = (
  state: UIModelState,
  parentContainerId: string
): ContainerRow[] => {
  const nestedContainers = baseSelectors.selectChildContainers(state, parentContainerId);
  return getContainerRows(nestedContainers, CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW);
};

/**
 * Checks if a row within nested containers is full
 *
 * @param state - UIModelState
 * @param parentContainerId - Parent container ID
 * @param rowIndex - Row index within the parent
 * @returns Whether the row is full
 */
export const selectIsNestedRowFull = (
  state: UIModelState,
  parentContainerId: string,
  rowIndex: number
): boolean => {
  const nestedContainers = baseSelectors.selectChildContainers(state, parentContainerId);
  return !canAddToRow(nestedContainers, rowIndex, CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW);
};

// ============================================================================
// Nesting Info Selectors
// ============================================================================

/**
 * Selects nesting information for a container
 *
 * @param state - UIModelState
 * @param containerId - Container ID
 * @returns Nesting information or null if container not found
 */
export const selectContainerNestingInfo = (
  state: UIModelState,
  containerId: string
) => {
  const canvasId = baseSelectors.selectActiveCanvasId(state);
  if (!canvasId) return null;

  const containers = baseSelectors.selectContainersForCanvas(state, canvasId);
  return getNestingInfo(containers, containerId);
};

/**
 * Checks if a container can have nested containers
 *
 * @param state - UIModelState
 * @param containerId - Container ID
 * @returns Whether the container can accept nested containers
 */
export const selectCanNestIn = (
  state: UIModelState,
  containerId: string
): boolean => {
  const nestingInfo = selectContainerNestingInfo(state, containerId);
  return nestingInfo?.canNest ?? false;
};

// ============================================================================
// Statistics Selectors
// ============================================================================

/**
 * Selects row statistics for a canvas
 *
 * @param state - UIModelState
 * @param canvasId - Canvas ID
 * @returns Row statistics
 */
export const selectRowStats = (
  state: UIModelState,
  canvasId: string
): RowStats => {
  const containers = baseSelectors.selectContainersForCanvas(state, canvasId);
  return getRowStats(containers, CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW);
};

/**
 * Selects row statistics for the active canvas
 *
 * @param state - UIModelState
 * @returns Row statistics or null
 */
export const selectActiveRowStats = (state: UIModelState): RowStats | null => {
  const canvasId = baseSelectors.selectActiveCanvasId(state);
  if (!canvasId) return null;
  return selectRowStats(state, canvasId);
};

// ============================================================================
// Factory Selectors (for useMemo)
// ============================================================================

/**
 * Creates a memoized selector for container rows
 */
export const makeSelectContainerRows = (canvasId: string) =>
  (state: UIModelState) => selectContainerRows(state, canvasId);

/**
 * Creates a memoized selector for checking if a row is full
 */
export const makeSelectIsRowFull = (canvasId: string, rowIndex: number) =>
  (state: UIModelState) => selectIsRowFull(state, canvasId, rowIndex);

/**
 * Creates a memoized selector for nested container rows
 */
export const makeSelectNestedContainerRows = (parentContainerId: string) =>
  (state: UIModelState) => selectNestedContainerRows(state, parentContainerId);

/**
 * Creates a memoized selector for row container count
 */
export const makeSelectRowContainerCount = (canvasId: string, rowIndex: number) =>
  (state: UIModelState) => selectRowContainerCount(state, canvasId, rowIndex);
